import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import {
  Table,
  TableHeader,
  TableRowHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
  GlobalStyle,
} from './style';
import groupBy from 'lodash/groupBy';

import { dateOnly } from '../../dates';
import ProgressBar from '../ProgressBar';
import { markTaskReopened, getSubmissionsByAssignmentId } from '../../service';
import { duplicateAssignment } from '../../utils/function';
import GeneralPopup from '../GeneralPopup';
import DeleteAssignmentPopup from '../DeleteAssignmentPopUp';
import { getUserRole } from '../../userLocalDetails';
import Toast from '../Toast';
import { toast } from 'react-toastify';

import QuestionTooltip from '../../components2/QuestionTooltip';
import { useResizedImage } from '../../components2/resizeImage';
import { groupByClass } from '../../service/tasks';
import { downloadAllSubmissionsZip } from '../Shared/helper/downloadPdf';
import RubricOutcomesPopup from '../RubricOutcomesPopup';
import KebabMenu from '../../components2/KebabMenu';
import ShareResourcePopup from '../../components2/ShareResourcePopup';
import ActionPopup from '../../components2/ActionPopup';
import { 
  TaskActionButton,
  TaskActionButtonIcon,
  TaskActionButtonText,
  TaskActionsContainer,
  KebabMenuContainer,
  TaskNameCell,
  ActionsCell,
  TaskNameHeaderCell,
  ClassesHeaderCell,
  ClosedOnHeaderCell,
  FeedbackMethodHeaderCell,
  SubmissionsHeaderCell,
  ActionsHeaderCell
} from './style';
function TaskHistoryDataComponent({ list }) {
  const history = useHistory();
  const [popupVisible, setPopupVisible] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [hoveredTaskId, setHoveredTaskId] = useState(null);
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [selectedTaskForRubric, setSelectedTaskForRubric] = useState(null);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [selectedTaskForShare, setSelectedTaskForShare] = useState(null);
  const [shareSuccess, setShareSuccess] = useState(null);
  const [selectedTaskIdForDuplicate, setSelectedTaskIdForDuplicate] = useState(null);
  const [isDuplicatingTask, setIsDuplicatingTask] = useState(false);

  const resizedImages = useResizedImage();
  const isTeacher = getUserRole() === 'TEACHER';
  const queryClient = useQueryClient();

  const handleViewTask = async (task) => {
    if (isTeacher && task.submissionCount > 0) {
      const submissions = await getSubmissionsByAssignmentId(task.id);
      if (submissions?.length > 0) {
        history.push(`/submissions/${submissions[0].id}`);
      } else {
        history.push(`/tasks/${task.id}`);
      }
    } else if (isTeacher) {
      history.push(`/tasks/${task.id}`);
    } else if (task.submissionId) {
      history.push(`/submissions/${task.submissionId}`);
    } else if (task.link) {
      window.location.href = task.link;
    }
  };

  const groupedTasks = groupByClass(list);
  function getReviewedByLabel(reviewedBy) {
    const labels = {
      TEACHER: 'Teacher (Manual)',
      JEDDAI: 'JeddAI',
      JEDDAI_ASSISTED: 'Teacher (AI-Assisted)',
      P2P: 'Peer to Peer',
    };

    return labels[reviewedBy] || 'Unknown';
  }

  const handleReopen = (taskId) => {
    if (!taskId) {
      setPopupVisible(true);
      return;
    }
    setSelectedTaskId(taskId);
    setPopupVisible(true);
  };

  const confirmReopen = async () => {
    try {
      const response = await markTaskReopened(selectedTaskId);
      if (response.status) {
        toast(<Toast message={'Task Reopened...'} />);
        queryClient.invalidateQueries(['tasks']);
        queryClient.invalidateQueries(['completedTasks']);
        queryClient.invalidateQueries(['assignments']);
        queryClient.removeQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) && query.queryKey[0] === 'submissionById',
        });
      } else {
        toast(<Toast message={'Failed to reopen task. Please try again.'} />);
      }
    } catch (error) {
      toast(<Toast message={'An error occurred while reopening the task.'} />);
    }
    setPopupVisible(false);
    setSelectedTaskId(null);
  };

  const handlePopupClose = () => {
    setPopupVisible(false);
    setSelectedTaskId(null);
  };

  const handleDeleteClick = (task) => {
    // Use the original task object with all class information
    const originalTask = list.find((t) => t.id === task.id);
    setTaskToDelete(originalTask);
    setDeletePopupVisible(true);
  };

  const hideDeletePopup = () => {
    setDeletePopupVisible(false);
    setTaskToDelete(null);
    queryClient.invalidateQueries(['completedTasks']);
    queryClient.invalidateQueries(['tasks']);
  };

  const handleDuplicateAssignment = (id) => {
    if (isDuplicatingTask) {
      return;
    }

    setSelectedTaskIdForDuplicate(id);
  };

  const confirmDuplicate = async () => {
    if (isDuplicatingTask || !selectedTaskIdForDuplicate) {
      return;
    }

    const taskIdToDuplicate = selectedTaskIdForDuplicate;
    setIsDuplicatingTask(true);
    setSelectedTaskIdForDuplicate(null);

    try {
      const duplicatedTask = await duplicateAssignment(taskIdToDuplicate, list);
      if (!duplicatedTask?.id) {
        throw new Error('Duplicated task id was not returned');
      }

      queryClient.setQueryData(['assignments'], (currentAssignments) => {
        if (!Array.isArray(currentAssignments)) {
          return currentAssignments;
        }

        const isAlreadyPresent = currentAssignments.some(
          (assignment) =>
            String(assignment.id) === String(duplicatedTask.id),
        );

        if (isAlreadyPresent) {
          return currentAssignments;
        }

        return [duplicatedTask, ...currentAssignments];
      });

      queryClient.invalidateQueries({
        queryKey: ['assignments'],
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: ['tasks'],
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: ['completedTasks'],
        refetchType: 'active',
      });

      toast(<Toast message={'Task Duplicated...'} />);
    } catch (error) {
      toast(<Toast message={'Failed to duplicate Task. Please try again.'} />);
    } finally {
      setIsDuplicatingTask(false);
    }
  };

  const handleDuplicatePopupClose = () => {
    setSelectedTaskIdForDuplicate(null);
  };

  const handleRubricOutcomesClick = (task) => {
   setSelectedTaskForRubric(task);
   };

  const hideRubricOutcomesPopup = () => {
   setSelectedTaskForRubric(null);
   };

  const handleShareClick = (task) => {
    setSelectedTaskForShare(task);
    setShowSharePopup(true);
  };

  const handleSharePopupClose = () => {
    setShowSharePopup(false);
    setSelectedTaskForShare(null);
  };

  const handleSharePopupSuccess = (people, successMessage) => {
    setShareSuccess({
      people,
      message: successMessage
    });
    handleSharePopupClose();
  };

  const handleShareSuccessPopupClose = () => {
    setShareSuccess(null);
  };

  return (
    <>
      <GlobalStyle />
      <Table>
        <TableHeader>
          <TableRowHeader>
            <TaskNameHeaderCell>
              Task Name
            </TaskNameHeaderCell>
            <ClassesHeaderCell>Classes</ClassesHeaderCell>
            <ClosedOnHeaderCell>
              Closed on
            </ClosedOnHeaderCell>
            {isTeacher && (
              <FeedbackMethodHeaderCell>
                Feedback Method
              </FeedbackMethodHeaderCell>
            )}
            {isTeacher && (
              <SubmissionsHeaderCell>
                Submissions
              </SubmissionsHeaderCell>
            )}
            <ActionsHeaderCell></ActionsHeaderCell>
          </TableRowHeader>
        </TableHeader>
        <tbody>
          {groupedTasks.map((task, index) => (
            <TableRow
              key={index}
              className="table-row"
              onMouseEnter={() => setHoveredTaskId(task.id)}
              onMouseLeave={() => setHoveredTaskId(null)}
              onClick={(e) => {
                if (e.target.tagName !== 'A') {
                  handleViewTask(task);
                }
              }}
            >
              <TaskNameCell>
                {task.title}
              </TaskNameCell>
              <TableCell>{task.classTitles}</TableCell>
              <TableCell>
                {task.closedAt || task.completedAt
                  ? dateOnly(task.closedAt || task.completedAt)
                  : ''}
              </TableCell>
              {isTeacher && (
                <TableCell>{getReviewedByLabel(task.reviewedBy)}</TableCell>
              )}
              {isTeacher && (
                <TableCell>
                  <ProgressBar
                    title={'Submissions'}
                    isPercentage={false}
                    count={task.submissionCount}
                    total={task.expectedSubmissions}
                  />
                  <ProgressBar
                    title={'Reviewed'}
                    isPercentage={false}
                    count={task.reviewCount}
                    total={task.submissionCount}
                  />
                </TableCell>
              )}
              <ActionsCell>
                <TaskActionsContainer>
                  {task.id && (
                    <>
                      <TaskActionButton
                        visible={hoveredTaskId === task.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleViewTask(task);
                        }}
                      >
                        <TaskActionButtonIcon src={resizedImages?.open} alt="View" />
                        <TaskActionButtonText>View</TaskActionButtonText>
                      </TaskActionButton>

                      {isTeacher && (
                        <>
                          <Tooltip title="Create a duplicate to edit" arrow placement="top">
                            <TaskActionButton
                              visible={hoveredTaskId === task.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleDuplicateAssignment(task.id);
                              }}
                            >
                              <TaskActionButtonIcon src={resizedImages?.duplicate || resizedImages?.copy} alt="Duplicate" />
                              <TaskActionButtonText>Duplicate</TaskActionButtonText>
                            </TaskActionButton>
                          </Tooltip>

                          <TaskActionButton
                            visible={hoveredTaskId === task.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleReopen(task.id);
                            }}
                          >
                            <TaskActionButtonIcon src={resizedImages?.reopen} alt="Reopen" />
                            <TaskActionButtonText>Reopen</TaskActionButtonText>
                          </TaskActionButton>

                          <KebabMenuContainer visible={hoveredTaskId === task.id}>
                            <KebabMenu 
                              menuItems={[
                                {
                                  label: 'View',
                                  icon: resizedImages?.open,
                                  action: () => handleViewTask(task)
                                },
                                {
                                  label: 'Duplicate',
                                  icon: resizedImages?.duplicate || resizedImages?.copy,
                                  action: () => handleDuplicateAssignment(task.id)
                                },
                                {
                                  label: 'Share',
                                  icon: resizedImages?.sharePurple,
                                  action: () => handleShareClick(task)
                                },
                                {
                                  label: 'Reopen',
                                  icon: resizedImages?.reopen,
                                  action: () => handleReopen(task.id)
                                },
                                {
                                  label: 'Download',
                                  icon: resizedImages?.export,
                                  action: () => downloadAllSubmissionsZip(task.id)
                                },
                                {
                                  label: 'Rubric Outcomes',
                                  icon: resizedImages?.rubric,
                                  action: () => handleRubricOutcomesClick(task)
                                },
                                {
                                  label: 'Delete',
                                  icon: resizedImages?.deleteNew,
                                  action: () => handleDeleteClick(task)
                                }
                              ]}
                            />
                          </KebabMenuContainer>
                        </>
                      )}
                    </>
                  )}
                </TaskActionsContainer>
              </ActionsCell>
            </TableRow>
          ))}
        </tbody>
      </Table>

      {popupVisible && (
        <GeneralPopup
          hidePopup={handlePopupClose}
          title="Reopen task"
          textContent={
           <> Are you sure you want to open reopen this task?</>
          }
          buttonText="Reopen"
          confirmButtonAction={confirmReopen}
          closeBtnText="Cancel"
          warningMessage= {
            <>
            Reopening this task will allow students to submit their work
            again, and you will be able to provide feedback. The task will be
            moved back to the Current Tasks page.
         
          </>
          }
          cancelPopup={handlePopupClose}
        />
      )}

      {deletePopupVisible && (
        <DeleteAssignmentPopup
          assignment={taskToDelete}
          hidedeletePopup={hideDeletePopup}
          deleteDocumentMutation={null}
          open={deletePopupVisible}
        />
      )}
      {selectedTaskForRubric && (
        <RubricOutcomesPopup
          assignmentId={selectedTaskForRubric?.id}
          hideAssignmentStatusPopup={hideRubricOutcomesPopup}
        />
      )}
      {showSharePopup && selectedTaskForShare && (
        <ShareResourcePopup
          open={showSharePopup}
          onClose={handleSharePopupClose}
          items={[{
            id: selectedTaskForShare.id,
            shareType: 'task',
            name: selectedTaskForShare.title
          }]}
          onShare={handleSharePopupSuccess}
        />
      )}
      {shareSuccess && (
        <ActionPopup
          open={!!shareSuccess}
          onClose={handleShareSuccessPopupClose}
          type="share"
          message={shareSuccess.message}
          emailList={shareSuccess.people}
          primaryButtonLabel="Awesome!"
          primaryButtonAction={handleShareSuccessPopupClose}
        />
      )}
      {selectedTaskIdForDuplicate && (
        <GeneralPopup
          hidePopup={handleDuplicatePopupClose}
          title="Duplicate task"
          textContent={
            <>Are you sure you want to duplicate this task?</>
          }
          buttonText="Duplicate"
          confirmButtonAction={confirmDuplicate}
          closeBtnText="Cancel"
          cancelPopup={handleDuplicatePopupClose}
        />
      )}
    </>
  );
}

export default TaskHistoryDataComponent;
